import type React from 'react';
import { useState, useImperativeHandle, useCallback } from 'react';
import { useKey, useLatest } from 'react-use';
import { removeEmptyTrailingPTags } from '@unifyapps/editor/editors/RichTextEditor/utils';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import getIsTextInputKey from './utils/getIsTextInputKey';
import type { DictationActionsRef } from './SpeechToTextAction';

type Props = {
  input: string | undefined;
  allowDictation: boolean;
  inputRef: React.RefObject<HTMLElement | null>;
  methodsRef?: React.RefObject<{
    onInputChange?: (newInput: string | undefined) => void;
    onInputSubmit?: () => void;
    onKeyDown?: (event?: KeyboardEvent | React.KeyboardEvent<HTMLTextAreaElement>) => void;
    hideMicrophone?: () => void;
    nativeKeyboardPress?: (keyValue?: string) => void;
  } | null>;
  dictationActionsRef?: DictationActionsRef;
};

export function useDictation(props: Props) {
  const { input, allowDictation, inputRef, methodsRef, dictationActionsRef } = props;
  const latestInputRef = useLatest(input);

  const [showMicrophone, setShowMicrophone] = useState(allowDictation);

  const hideMicrophone = useCallback(() => {
    dictationActionsRef?.current?.stopListening();
    setShowMicrophone(false);
  }, [dictationActionsRef]);

  useKey(getIsTextInputKey({ input: latestInputRef.current }), hideMicrophone, {
    event: 'keydown',
    target: inputRef.current,
  });

  useImperativeHandle(methodsRef, () => ({
    onInputChange: (newInput: string) => {
      if (!removeEmptyTrailingPTags(newInput) && allowDictation) {
        setShowMicrophone(true);
      }
    },
    onInputSubmit: () => {
      if (allowDictation) {
        hideMicrophone();
      }
    },
    hideMicrophone,
    onKeyDown: (event: KeyboardEvent) => {
      // On Backspace key press, maintain the previous state, else on pressing Backspace in empty text, the microphone will get hidden.
      if (event.keyCode !== 8) hideMicrophone();
    },
    onPaste: () => {
      hideMicrophone();
    },
    nativeKeyboardPress: (keyValue?: string) => {
      // On Backspace key press, maintain the previous state, else on pressing Backspace in empty text, the microphone will get hidden.
      // When we have no text and user presses backspace the event is fired and mic gets hidden. To handle this we are adding the check.
      if (keyValue !== 'Backspace') hideMicrophone();
    },
  }));

  const onSpeechTimeout = useEventCallback(() => {
    if (allowDictation) {
      setShowMicrophone(true);
    }
  });

  return {
    showMicrophone,
    hideMicrophone,
    onSpeechTimeout,
  };
}
